<template>
  <v-dialog v-model="showModal" max-width="900px" persistent scrollable>
    <v-card>
      <v-toolbar dark class="primary">
        <v-btn icon @click.native="showModal = false" dark><v-icon>fa-times</v-icon></v-btn>
        <v-toolbar-title>{{ id ? 'Szablon dokumentu (szkic)' : 'Nowy szablon dokumentu' }}</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-4">
        <v-progress-linear :indeterminate="true" v-if="loading"></v-progress-linear>
        <v-row>
          <v-col cols="4" md="3"><v-text-field v-model="form.code" label="Kod (np. UP-DOK)" outlined dense :disabled="!!id"></v-text-field></v-col>
          <v-col cols="4" md="2"><v-text-field v-model="form.version" label="Wersja" outlined dense :disabled="!!id"></v-text-field></v-col>
          <v-col cols="12" md="7"><v-text-field v-model="form.title" label="Tytuł dokumentu" outlined dense></v-text-field></v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4"><v-select v-model="form.legalFormRequirement" :items="legalForms" label="Wymagana forma prawna" outlined dense></v-select></v-col>
          <v-col cols="12" md="4"><v-select v-model="form.captureMethod" :items="captureMethods" label="Metoda zebrania" outlined dense></v-select></v-col>
          <v-col cols="12" md="4"><v-select v-model="form.collectionContext" :items="contexts" label="Kontekst (gdzie wolno)" outlined dense></v-select></v-col>
        </v-row>
        <v-alert type="warning" dense text v-if="form.legalFormRequirement === 'WRITTEN' && form.captureMethod === 'DRAWN_SIGNATURE'">
          Forma pisemna nie może być zbierana podpisem graficznym na tablecie - publikacja zostanie odrzucona.
        </v-alert>
        <v-row>
          <v-col cols="12" md="6">
            <v-select v-model="form.signerRoles" :items="roles" label="Dozwolone role podpisujących" multiple chips small-chips outlined dense></v-select>
          </v-col>
          <v-col cols="6" md="3"><v-checkbox v-model="form.requiresDualConsent" label="Zgoda podwójna" hide-details></v-checkbox></v-col>
          <v-col cols="6" md="3"><v-checkbox v-model="form.refusalAllowed" label="Odmowa dopuszczalna" hide-details></v-checkbox></v-col>
        </v-row>
        <v-textarea v-model="form.notes" label="Notatki (np. podstawa prawna, kto zatwierdził treść)" outlined dense rows="2"></v-textarea>

        <v-file-input v-model="docxFile" :label="id ? 'Nowy plik Word (.docx) - opcjonalnie podmień' : 'Plik Word (.docx) ze źródłem treści'"
          accept=".docx" prepend-icon="fa-file-word" outlined dense show-size :loading="detecting" @change="onDocxSelected"
          :hint="id ? 'Pozostaw puste, aby nie zmieniać obecnego pliku' : ('Dokument .docx z placeholderami, np. ' + ph('patientName') + '. Pola wykryją się automatycznie.')"
          persistent-hint></v-file-input>
        <v-alert v-if="detectMsg" :type="detectType" dense text class="mt-2 mb-0">{{ detectMsg }}</v-alert>

        <div class="subtitle-1 mt-2">Pola formularza <span class="grey--text caption">- placeholdery {{ ph('nazwa') }} w pliku Word</span></div>
        <v-simple-table dense>
          <thead><tr><th>Nazwa (w Word)</th><th>Etykieta na tablecie</th><th>Typ</th><th>Wymagane</th><th>Max dł. / opcje (po przecinku)</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(f, i) in form.fields" :key="i">
              <td><v-text-field v-model="f.name" dense hide-details placeholder="authorizedPerson"></v-text-field></td>
              <td><v-text-field v-model="f.label" dense hide-details placeholder="Osoba upoważniona"></v-text-field></td>
              <td><v-select v-model="f.type" :items="fieldTypes" dense hide-details></v-select></td>
              <td><v-checkbox v-model="f.required" dense hide-details></v-checkbox></td>
              <td>
                <v-text-field v-if="f.type === 'text'" v-model.number="f.maxLength" type="number" dense hide-details placeholder="120"></v-text-field>
                <v-text-field v-else-if="f.type === 'choice'" v-model="f.optionsText" dense hide-details placeholder="Opcja A, Opcja B"></v-text-field>
              </td>
              <td><v-icon small @click="form.fields.splice(i, 1)" title="Usuń pole">fa-trash-alt</v-icon></td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-btn small text color="primary" class="mt-1" @click="addField"><v-icon small left>fa-plus</v-icon> Dodaj pole</v-btn>
        <div class="grey--text caption mt-2">
          Wbudowane placeholdery: {{ ph('patientName') }}, {{ ph('peselMasked') }} (na tablecie zamaskowany, w PDF pełny),
          {{ ph('signature:1') }} (miejsce obrazu podpisu), {{ ph('metryka') }} (metryka procesu).
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="showModal = false">Anuluj</v-btn>
        <v-btn color="primary" :loading="loading" @click="save">Zapisz szkic</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { podpisyService, podpisyLabels, abpError } from "@/services/podpisy.service";
import { mapActions } from "vuex";

function toItems(map) {
  return Object.keys(map).map(k => ({ value: k, text: map[k] }));
}

export default {
  name: "AddEditSigningTemplate",
  props: ["id", "visible"],
  data() {
    return {
      loading: false,
      docxFile: null,
      detecting: false,
      detectMsg: "",
      detectType: "info",
      form: this.emptyForm(),
      legalForms: toItems(podpisyLabels.legalForm),
      captureMethods: toItems(podpisyLabels.captureMethod).filter(x => ["DRAWN_SIGNATURE", "CONFIRMATION"].indexOf(x.value) >= 0),
      contexts: toItems(podpisyLabels.context),
      roles: toItems(podpisyLabels.signerRole),
      fieldTypes: [{ value: "text", text: "tekst" }, { value: "checkbox", text: "tak/nie" }, { value: "choice", text: "wybór" }],
    };
  },
  computed: {
    showModal: {
      get() { return this.visible; },
      set(v) { if (!v) this.$emit("close"); },
    },
  },
  methods: {
    ...mapActions("alert", ["error", "success"]),
    emptyForm() {
      return {
        code: "", version: "1.0", title: "", legalFormRequirement: "DOCUMENTARY", captureMethod: "DRAWN_SIGNATURE",
        collectionContext: "RECEPTION", signerRoles: ["PATIENT"], requiresDualConsent: false, refusalAllowed: false, notes: "", fields: [],
      };
    },
    ph(name) { return "{{" + name + "}}"; },
    addField() {
      this.form.fields.push({ name: "", label: "", type: "text", required: true, maxLength: 120, optionsText: "" });
    },
    humanize(name) {
      const s = String(name || "").replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    onDocxSelected(file) {
      this.detectMsg = "";
      if (!file) return;
      this.detecting = true;
      podpisyService.analyzeTemplateDocx(file).then(resp => {
        const r = resp.data.result;
        const existing = this.form.fields.map(f => (f.name || "").trim());
        const added = [];
        (r.customPlaceholders || []).forEach(name => {
          if (existing.indexOf(name) >= 0) return;
          this.form.fields.push({ name: name, label: this.humanize(name), type: "text", required: true, maxLength: 120, optionsText: "" });
          added.push(name);
        });
        const builtIns = (r.builtInPlaceholders || []).join(", ");
        const parts = [];
        if (added.length) parts.push("Dodano pola z placeholderów: " + added.join(", ") + ". Sprawdź etykiety i typy.");
        else if ((r.customPlaceholders || []).length) parts.push("Wszystkie placeholdery mają już pola.");
        else parts.push("Nie wykryto własnych placeholderów {{...}} w pliku.");
        if (builtIns) parts.push("Wbudowane: " + builtIns + " (bez definicji pola).");
        if (r.warnings && r.warnings.length) parts.push(r.warnings.join(" "));
        this.detectType = added.length || (r.customPlaceholders || []).length ? "success" : "info";
        this.detectMsg = parts.join(" ");
      }).catch(e => { this.detectType = "warning"; this.detectMsg = abpError(e, "Nie można przeanalizować pliku - pola dodaj ręcznie."); })
        .finally(() => { this.detecting = false; });
    },
    load() {
      if (!this.id) { this.form = this.emptyForm(); return; }
      this.loading = true;
      podpisyService.getTemplate(this.id).then(resp => {
        const t = resp.data.result;
        this.form = {
          code: t.code, version: t.version, title: t.title, legalFormRequirement: t.legalFormRequirement, captureMethod: t.captureMethod,
          collectionContext: t.collectionContext, signerRoles: (t.signerRolesAllowed || "PATIENT").split(","),
          requiresDualConsent: t.requiresDualConsent, refusalAllowed: t.refusalAllowed, notes: t.notes,
          fields: (t.fields || []).map(f => ({ name: f.name, label: f.label, type: f.type, required: f.required, maxLength: f.maxLength,
            optionsText: (f.options || []).join(", ") })),
        };
      }).catch(e => this.error(abpError(e, "Nie można pobrać szablonu")))
        .finally(() => { this.loading = false; });
    },
    save() {
      const payload = {
        id: this.id || undefined,
        code: this.form.code, version: this.form.version, title: this.form.title,
        legalFormRequirement: this.form.legalFormRequirement, captureMethod: this.form.captureMethod,
        collectionContext: this.form.collectionContext, signerRolesAllowed: this.form.signerRoles.join(","),
        requiresDualConsent: this.form.requiresDualConsent, refusalAllowed: this.form.refusalAllowed, notes: this.form.notes,
        fields: this.form.fields.map(f => ({
          name: (f.name || "").trim(), label: (f.label || "").trim(), type: f.type, required: !!f.required,
          maxLength: f.type === "text" && f.maxLength ? Number(f.maxLength) : null,
          options: f.type === "choice" ? (f.optionsText || "").split(",").map(s => s.trim()).filter(s => s) : null,
        })),
      };
      this.loading = true;
      const isNew = !this.id;
      const req = isNew ? podpisyService.createTemplateDraft(payload) : podpisyService.updateTemplateDraft(payload);
      req.then(resp => {
        const saved = resp.data.result;
        if (this.docxFile) {
          return podpisyService.uploadTemplateDocx(saved.id, this.docxFile).then(up => {
            this.success("Szkic zapisany, plik Word wgrany");
            this.$emit("saved", up.data.result || saved);
          });
        }
        this.success("Szkic zapisany");
        this.$emit("saved", saved);
      }).catch(e => this.error(abpError(e, "Nie można zapisać szablonu")))
        .finally(() => { this.loading = false; });
    },
  },
  mounted() {
    this.load();
  },
};
</script>
